import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import { getAttendanceList, getAttendanceStaffList } from "../../../services/attendance";
import moment from "moment";
import Select from "react-select";
import ImageUrl from "../../../ImageUrl";
import exportUrl from "../../../exportUrl";

// Km photos are saved under public/ now, so the stored path already says where
// it is. Rows from before that change hold a path relative to the 'public'
// storage disk, which is only reachable through the /storage symlink — keep
// serving those the old way so they still open wherever the symlink exists.
const vehicleImageUrl = (path) =>
    path?.startsWith("images/")
        ? `${ImageUrl}/${path}`
        : `${ImageUrl}/storage/${path}`;

export default function AttendanceList() {
    const [attendances, setAttendances] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Filters
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [fromDate, setFromDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
    const [selectedStatus, setSelectedStatus] = useState("");

    const columnDefs = [
        {
            headerName: "Date",
            field: "date",
            width: 130,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: function (params) {
                return params.value ? moment(params.value).format("DD/MM/YYYY") : "-";
            }
        },
        {
            headerName: "Staff Name",
            field: "user.name",
            width: 180,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            valueGetter: function (params) {
                return params.data.user?.name || "N/A";
            }
        },
        // clock_in_time / clock_out_time come from the API already formatted as
        // railway time in the company's timezone. The raw clock_in / clock_out
        // are sent as UTC timestamps, so formatting those in the browser would
        // shift the time by whatever the viewer's PC timezone happens to be —
        // a 18:00 auto clock out could read 23:30 on the same screen.
        {
            headerName: "Clock In",
            field: "clock_in_time",
            width: 130,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: function (params) {
                return params.value || "-";
            }
        },
        {
            headerName: "Clock Out",
            field: "clock_out_time",
            width: 130,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: function (params) {
                return params.value || "-";
            }
        },
        {
            headerName: "Working Hours",
            field: "working_hours",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: function (params) {
                return params.value ? params.value + " hrs" : "-";
            }
        },
        {
            headerName: "Vehicle KM From",
            field: "vehicle_km_from",
            width: 180,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: (params) => {
                const attendance = params.data;

                return (
                    <div>
                        {attendance?.vehicle_km_from_image ? (
                            <a
                                href={vehicleImageUrl(attendance.vehicle_km_from_image)}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-sm btn-info me-1"
                            >
                                View
                            </a>
                        ) : (
                            "-"
                        )}

                        {attendance?.vehicle_km_from && (
                            <div className="small">
                                {attendance.vehicle_km_from} km
                            </div>
                        )}
                    </div>
                );
            }


        },
        {
            headerName: "Vehicle KM To",
            field: "vehicle_km_to",
            width: 180,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: (params) => {
                const attendance = params.data;

                return (
                    <div>
                        {attendance?.vehicle_km_to_image ? (
                            <a
                                href={vehicleImageUrl(attendance.vehicle_km_to_image)}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-sm btn-info me-1"
                            >
                                View
                            </a>
                        ) : (
                            "-"
                        )}

                        {attendance?.vehicle_km_to && (
                            <div className="small">
                                {attendance.vehicle_km_to} km
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            headerName: "Status",
            field: "status",
            width: 140,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: function (params) {
                if (params.value === "clocked_in") {
                    return <span className="badge bg-warning">Clocked In</span>;
                } else {
                    return <span className="badge bg-success">Clocked Out</span>;
                }
            }
        },
        {
            headerName: "Actions",
            field: "id",
            width: 200,
            sortable: false,
            resizable: true,
            filter: false,
            floatingFilter: false,
            cellStyle: { textAlign: "center" },
            cellRenderer: function (params) {
                const attendance = params.data;
                return (
                    <div>
                        {attendance?.vehicle_km_from_image && (
                            <button
                                className="btn btn-sm btn-primary me-1"
                                onClick={() =>
                                    window.open(
                                        vehicleImageUrl(attendance.vehicle_km_from_image),
                                        "_blank"
                                    )
                                }
                            >
                                View From
                            </button>
                        )}

                        {attendance?.vehicle_km_to_image && (
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={() =>
                                    window.open(
                                        vehicleImageUrl(attendance.vehicle_km_to_image),
                                        "_blank"
                                    )
                                }
                            >
                                View To
                            </button>
                        )}

                        {!attendance?.vehicle_km_from_image &&
                            !attendance?.vehicle_km_to_image && "-"}
                    </div>
                );
            }
        }
    ];

    useEffect(() => {
        loadStaffList();
    }, []);

    useEffect(() => {
        loadAttendanceList();
    }, [currentPage, selectedStaff, fromDate, toDate, selectedStatus]);

    const loadStaffList = async () => {
        try {
            const res = await getAttendanceStaffList();
            if (res && res.status) {
                const staffOptions = res.data.staff.map(staff => ({
                    value: staff.id,
                    label: staff.name 
                    // + " (" + staff.email + ")"
                }));
                setStaffList(staffOptions);
            }
        } catch (error) {
            toast.error("Error loading staff list");
        }
    };

    const loadAttendanceList = async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                per_page: 20
            };

            if (selectedStaff) {
                params.user_id = selectedStaff.value;
            }
            if (fromDate) {
                params.from_date = fromDate;
            }
            if (toDate) {
                params.to_date = toDate;
            }
            if (selectedStatus) {
                params.status = selectedStatus;
            }

            const res = await getAttendanceList(params);
            if (res && res.status) {
                setAttendances(res.data.attendances.data);
                setTotalPages(res.data.attendances.last_page);
            }
        } catch (error) {
            toast.error("Error loading attendance list");
        }
        setLoading(false);
    };

    const handleFilter = () => {
        setCurrentPage(1);
        loadAttendanceList();
    };

    const handleReset = () => {
        setSelectedStaff(null);
        setFromDate(moment().startOf('month').format('YYYY-MM-DD'));
        setToDate(moment().format('YYYY-MM-DD'));
        setSelectedStatus("");
        setCurrentPage(1);
    };

    // Downloads the whole filtered result, not just the page on screen, so the
    // report matches the filters rather than the pagination.
    const downloadfile = (dtype) => {
        const params = new URLSearchParams({
            from_date: fromDate || "",
            to_date: toDate || "",
            user_id: selectedStaff ? selectedStaff.value : "",
            status: selectedStatus || "",
            branch: localStorage.getItem("branch") || "",
            type: dtype,
        });
        window.open(
            `${exportUrl}report/attendancedownload?${params.toString()}`,
            "_blank",
            "noopener"
        );
    };

    const statusOptions = [
        { value: "", label: "All" },
        { value: "clocked_in", label: "Clocked In" },
        { value: "clocked_out", label: "Clocked Out" }
    ];

    return (
        <div className="app-content main-content">
            <div className="side-app main-container">
                <div className="page-header d-xl-flex d-block">
                    <div className="page-leftheader">
                        <div className="page-title">Attendance List</div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xl-12 col-lg-12 col-md-12 col-xm-12">
                        <div className="card">
                            <div className="card-header">
                                <h4 className="card-title">Filters</h4>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>Staff</label>
                                            <Select
                                                options={staffList}
                                                value={selectedStaff}
                                                onChange={setSelectedStaff}
                                                isClearable
                                                placeholder="Select Staff"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group">
                                            <label>From Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={fromDate}
                                                onChange={(e) => setFromDate(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group">
                                            <label>To Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={toDate}
                                                onChange={(e) => setToDate(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group">
                                            <label>Status</label>
                                            <Select
                                                options={statusOptions}
                                                value={statusOptions.find(opt => opt.value === selectedStatus)}
                                                onChange={(opt) => setSelectedStatus(opt.value)}
                                                placeholder="Select Status"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>&nbsp;</label>
                                            <div>
                                                <button
                                                    className="btn btn-primary me-2"
                                                    onClick={handleFilter}
                                                >
                                                    Apply Filter
                                                </button>
                                                <button
                                                    className="btn btn-secondary me-2"
                                                    onClick={handleReset}
                                                >
                                                    Reset
                                                </button>
                                                <button
                                                    className="btn btn-danger me-2"
                                                    onClick={() => downloadfile("pdf")}
                                                >
                                                    Download PDF
                                                </button>
                                                <button
                                                    className="btn btn-success"
                                                    onClick={() => downloadfile("excel")}
                                                >
                                                    Download Excel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-header">
                                <h4 className="card-title">Attendance Records</h4>
                            </div>
                            <div className="card-body">
                                {loading ? (
                                    <div className="text-center">
                                        <div className="spinner-border" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="ag-theme-alpine ag-style mt-2">
                                        <AgGridReact
                                            columnDefs={columnDefs}
                                            rowData={attendances}
                                            pagination={true}
                                            paginationPageSize={20}
                                            sortingOrder={["asc", "desc"]}
                                            suppressDragLeaveHidesColumns={true}
                                            domLayout="autoHeight"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
